import { getKvSyncApi } from '@/api/kvSync'
import sentry from '@/services/sentry'
import config from '@/config'
import { currentUtcMonth, buildMonthlyCountKey } from '@/util/monthlyParticipant'

/**
 * Reads the backfilled count off a kvSync HTTP response. The counting KV
 * value can arrive as a JSON string or an already-parsed object, mirroring
 * the shape handled by the previous MonthlyParticipantUsage container.
 */
const readTotal = (resp) => {
  const raw = resp?.body?.value
  const value = typeof raw === 'string' ? JSON.parse(raw) : raw
  return Number.isFinite(value?.total) ? value.total : 0
}

/**
 * Parses a live count update delivered by the kvSync MQTT subscription.
 * Returns NaN (instead of throwing) for a malformed payload so the caller
 * can guard with Number.isFinite and leave the last known count in place.
 */
const safeParseTotal = (message) => {
  try {
    return JSON.parse(message)?.total
  } catch (e) {
    sentry.captureException(e)
    return NaN
  }
}

/**
 * A factory (not a plain object) so every Vuex.Store instance — including
 * the isolated stores created per test — gets its own state rather than
 * sharing one module-scoped object.
 */
const createState = () => ({
  used: 0,
  subscribed: false,
  subscribedKey: null,
  trialActive: false,
})

/**
 * Non-production only: lets QA preview any meter state (e.g. "N left", or the
 * 80%/warning and 90%/danger fill colours) without a real usage history, by
 * appending ?mppUsed=<n> to the URL. Returns NaN in production or when the
 * param is absent/invalid, so the real live count is used untouched.
 */
const readUsedPreviewOverride = () => {
  if (config.env === 'production') return NaN
  try {
    const raw = new URLSearchParams(window.location.search).get('mppUsed')
    if (raw === null) return NaN
    const parsed = Number(raw)
    return Number.isFinite(parsed) ? parsed : NaN
  } catch (e) {
    return NaN
  }
}

const getters = {
  used: (state) => {
    const preview = readUsedPreviewOverride()
    return Number.isFinite(preview) ? preview : state.used
  },
  limit: (_, __, ___, rootGetters) => rootGetters.monthlyParticipantLimit,
  isVisible: (_, __, rootState, rootGetters) => (
    Number.isFinite(rootGetters.monthlyParticipantLimit) && Boolean(rootState.currentUser && rootState.currentUser.id)
  ),
  trialActive: (state) => state.trialActive,
}

const mutations = {
  SET_USED(state, n) {
    Object.assign(state, { used: n })
  },
  SET_SUBSCRIPTION(state, key) {
    Object.assign(state, { subscribed: true, subscribedKey: key })
  },
  RESET_SUBSCRIPTION(state) {
    Object.assign(state, { subscribed: false, subscribedKey: null })
  },
  SET_TRIAL_ACTIVE(state, v) {
    Object.assign(state, { trialActive: Boolean(v) })
  },
}

const actions = {
  /**
   * Backfills the presenter's monthly participant count from the
   * non-retained counting KV, then subscribes for live updates. Idempotent
   * so every meter reader (plan card, header) can safely dispatch it on
   * mount while only one HTTP backfill + subscription is ever created.
   *
   * No-ops (without locking) until the treatment-arm is ready — i.e. a
   * finite monthlyParticipantLimit and a logged-in user — so it can be
   * re-dispatched once planBenefit/currentUser arrive late, and never
   * builds a key with an undefined userId.
   *
   * The subscription lock (SET_SUBSCRIPTION) is committed SYNCHRONOUSLY,
   * before the HTTP backfill's await. Two meters (header + card) can both
   * dispatch init in the same tick; without a synchronous lock both would
   * pass the readiness check before either finishes backfilling, and both
   * would subscribe — kvSync's subscriber pushes callbacks without
   * deduping, so the key would end up with two live MQTT callbacks.
   */
  async init({
    state, commit, dispatch, rootState, rootGetters,
  }) {
    if (state.subscribed) return
    const isReady = Number.isFinite(rootGetters.monthlyParticipantLimit)
      && Boolean(rootState.currentUser && rootState.currentUser.id)
    if (!isReady) return

    const key = buildMonthlyCountKey(
      rootGetters['liveStatsV2/liveStatsVersionPrefix'],
      config.livestatsTopicPrefix,
      rootState.currentUser.id,
      currentUtcMonth(),
    )

    commit('SET_SUBSCRIPTION', key)

    try {
      const resp = await getKvSyncApi().getValueByKey(key)
      commit('SET_USED', readTotal(resp))
    } catch (e) {
      sentry.captureException(e)
    }

    dispatch('kvSync/subscribeKeyAction', {
      key,
      callback: (topic, message) => {
        const total = safeParseTotal(message)
        if (Number.isFinite(total)) commit('SET_USED', total)
      },
    }, { root: true })
  },

  /**
   * Tears down the shared subscription. Not called by either meter on
   * unmount — the header keeps it alive app-wide — but kept available for
   * callers (e.g. logout) that need to release it explicitly.
   */
  async teardown({ state, commit, dispatch }) {
    if (!state.subscribed) return
    await dispatch('kvSync/unsubscribeKeyAction', state.subscribedKey, { root: true })
    commit('RESET_SUBSCRIPTION')
  },
}

export default {
  namespaced: true,
  state: createState,
  getters,
  mutations,
  actions,
}
